import React, { useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';

import GetPriceHero from '../GetPriceHero';
import WhatMattersMost from '../WhatMattersMost';
import AsSeenIn from '../AsSeenIn';
import FAQSection from '../FAQSection';
import FlowerCTA from '../FlowerCTA';
import FooterSection from '../Footer/FooterSection';
import LoaderComponent from '@evdy/web-core/dist/components/shared/elements/LoaderComponent';
import WhyWereSpecial from '../WhyWereSpecial';
import SimpleSteps from '../SimpleSteps';
import Stars from '../Stars';

import { useHideIntercom, useRedirectToLastScreen } from '../../customHooks';
import { useScrollToTop } from '@evdy/web-core/dist/customHooks';
import { preneedPackagesOperations } from '@evdy/web-redux/dist/lib/webPurchase/preneedPackages';
import { authUserOperations } from '@evdy/web-redux/dist/lib/authUser';
import { getAPIUrl } from '@evdy/web-redux/dist/constants';
import { getDecodedURIEmail } from '@evdy/web-core/dist/lib/utils';

import styles from './GetMyEstimate.module.scss';

import whatMattersMostTwo from '../../public/images/what-matters-most-two.png';
import whatMattersMostThree from '../../public/images/what-matters-most-three.png';
import whatMattersMostFour from '../../public/images/what-matters-most-four.png';
import doloresH from '../../public/images/dolores-h-profile.jpg';
import sallyN from '../../public/images/YA-GIRL-SALLY.jpg';

const { getPreneedPackages } = preneedPackagesOperations;
const { createPreneedUser, resetPlanPackageUpdated, saveLastScreen } = authUserOperations;

const {
  'get-my-estimate': getMyEstimateClass,
  'reviews-section': reviewsSectionClass,
  'reviews-container': reviewsContainerClass,
  review: reviewClass,
  reviewer: reviewerClass
} = styles;

const API_URL_3000 = getAPIUrl('', 3000);

const GetMyEstimate = ({ location }) => {
  const dispatch = useDispatch();
  const { authUser, preneedPackages = [] } = useSelector(({ authUser, webPurchase }) => ({
    authUser,
    preneedPackages: webPurchase.preneedPackages.data?.packages
  }));
  //redux for authUser/redirect after succesful form submission
  const { preneedUserCreated, planPackageUpdated, isFetching } = authUser ?? {};
  const userData = authUser?.data ?? {};
  const { _id: loggedInUserId, preneed } = userData ?? {};
  const { userInfo } = preneed ?? {};
  const { dateOfBirth, address } = userInfo ?? {};
  const isLoggedIn = !!loggedInUserId;

  const { query, search: rawQueryString } = location ?? {};
  const { fn, ln, email, birthday, zip, name, preselectedcompany } = query ?? {};

  const hasQueryParams = ((!!fn && !!ln) || !!name) && !!email;

  // if coming from unbounce and sufficient user info is passed via query params, create user and send to flow
  useEffect(() => {
    if (hasQueryParams) {
      const birthMonth = birthday?.substring(0, 2);
      const birthDay = birthday?.substring(2, 4);
      const birthYear = birthday?.substring(4, 8);
      // handle name passed as single input
      const fName = name?.split(' ')[0];
      const lName = name?.split(' ').slice(1).join(' ');

      const decodedEmail = getDecodedURIEmail(rawQueryString);

      dispatch(
        createPreneedUser({
          firstName: fn || fName,
          lastName: ln || lName,
          email: decodedEmail,
          ...(zip && { zipcode: zip }),
          ...(birthDay && { birthDay }),
          ...(birthMonth && { birthMonth }),
          ...(birthYear && { birthYear }),
          ...(preselectedcompany && { preselectedcompany })
        })
      );
    }
  }, [
    hasQueryParams,
    preselectedcompany,
    dispatch,
    birthday,
    name,
    fn,
    ln,
    email,
    zip,
    rawQueryString
  ]);

  // get preneed packages
  // unneeded - keep for reference if page is brought back
  useEffect(() => {
    // if (!preneedPackages?.length) {
    //   dispatch(getPreneedPackages());
    // }
  }, [preneedPackages.length]);

  // redirect to /plan/details/a with after user created
  useEffect(() => {
    if (preneedUserCreated && hasQueryParams) window.location = `${API_URL_3000}/plan/details/a`;
  }, [preneedUserCreated, hasQueryParams]);

  // redirect to `/plan/home` if not creating an unbounce user
  useEffect(() => {
    if (!preneedUserCreated && !hasQueryParams) window.location = `${API_URL_3000}/plan/home`;
  }, [preneedUserCreated, hasQueryParams]);

  // Hide Intercom on mobile
  useHideIntercom(768);

  // remove redirect for user.lastScrren - logic deprecated and is as a landing page
  // useRedirectToLastScreen({});

  //scroll to top on mount
  useScrollToTop();

  const reviews = [
    {
      name: 'Sally N.',
      image: sallyN,
      stars: 5,
      copy: 'I kept putting it off, but knew I should get these decisions written down. Everdays made it so easy, and I had a nice plan I could email to my kids in less than 10 minutes.'
    },
    {
      name: 'Dolores H.',
      image: doloresH,
      stars: 5,
      copy: 'Everdays made it so easy to take care of my cremation, there was no reason not to do it. Now it’s done and my kids have what they need all ready when they need it.'
    }
  ];

  return (
    <div className={getMyEstimateClass}>
      <GetPriceHero {...{ preneedPackages, isLoggedIn, userData, location }} />
      <SimpleSteps />
      <hr />
      <div className={reviewsSectionClass}>
        <h2>What our customers say</h2>
        <div className={reviewsContainerClass}>
          {reviews.map(review => (
            <div className={reviewClass} key={review.name}>
              <Stars stars={review.stars} />
              <p>"{review.copy}"</p>
              <div className={reviewerClass}>
                <img src={review.image} alt="" />
                <h5>{review.name}</h5>
              </div>
            </div>
          ))}
        </div>
      </div>
      <WhyWereSpecial />
      <WhatMattersMost
        imgArray={[
          {
            url: whatMattersMostThree,
            title: 'Family',
            subtitle: 'Free your loved ones from stressful choices and unforeseen expenses.'
          },
          {
            url: whatMattersMostTwo,
            title: 'Save 40%',
            subtitle: 'Buying now saves you & your family thousands of dollars.*'
          },
          {
            url: whatMattersMostFour,
            title: 'Insurance',
            subtitle: 'Take comfort in knowing your family is covered from day one.†'
          }
        ]}
      />
      <AsSeenIn />
      <FAQSection />
      <FlowerCTA />
      <FooterSection
        firstAsteriskCopy="*Based on historical inflation rates and typical life expectancy."
        secondAsteriskCopy="†If you choose our insurance plan, full coverage goes into effect no later than 24 months from your first payment. Until then, your family is protected but at a lesser amount."
      />
      {isFetching && <LoaderComponent />}
    </div>
  );
};

export default GetMyEstimate;
